import { z } from 'zod'

/** All panel component types available in the app. */
export const ComponentTypeSchema = z.enum([
  'chart',
  'bar-chart',
  'scanner',
  'multi-scanner',
  'key-stats',
  'watchlist',
  'news',
  'ticker-stats',
  'calendar',
  'history-records',
  'history-chart',
  'history-stats',
  'history-news',
  'history-comparison',
  'history-time-series',
  'history-scatter',
  'history-bar',
  'history-bar-category',
  'history-donut-category',
  // NOTE: 'history-deep-dive' is retired (2026-09-09 entitlements audit, D11:
  // Deep Dive and Deep Research are the same product). It stays in the schema
  // for graceful degradation of persisted layouts. `idb-cache.getWorkspaceDoc`
  // drops the WHOLE cached document when any windowConfig fails to parse, so
  // removing the member would cost a saved layout, not just the one panel.
  'history-deep-dive',
  'history-intraday',
  'history-calendar',
  'history-multi-chart',
  'history-sankey',
  'history-heatmap',
  'history-time-distribution',
  // NOTE: 'history-briefing' is kept in the schema for graceful degradation of
  // persisted layouts. The panel type is no longer registered in the registry or
  // surfaced in add-panel menus — stored configs that reference it fall back to
  // PlaceholderPanel rather than causing a schema parse failure.
  'history-briefing',
  'history-scorecard',
  'tags-explorer',
  'notebook',
  'alert-log',
  // Price alerts over edge-realtime (staging build gate REALTIME_WEBSOCKET_ENABLED). Kept in
  // the schema whatever the gate says, so a saved layout always parses.
  'price-alerts',
  // Alert Window over edge-realtime (same gate as price-alerts; kept in the schema regardless).
  'alert-windows',
  'multi-chart',
  'sector-heatmap',
  'theme-tracker',
  // Market-wide regime panel: one house-computed reading of the gap-up
  // universe, identical for every account, and the content of the fixed Home
  // view. Not ticker-scoped, so it ignores `symbolIndex`.
  'market-overwatch',
  // EdgeScript editor as its own dashboard panel (phase 2 Task 2.10; dev-gated).
  'script-editor',
  // Scanner parity (dev tool): one scanner request against the database and
  // real time. Offered only with the PostHog dev flag devtools-scanner-parity.
  'scanner-parity',
  // ── Backtest report panels ─────────────────────────────────────────────────
  // 'backtest-metrics' is the Key Metrics strip: a Run-mode tile (the top row
  // of the Run Overview preset); the strategy Report page renders the same
  // strip above its mosaic instead.
  'backtest-metrics',
  'backtest-overview',
  'backtest-performance',
  'backtest-trades',
  'backtest-calendar',
  'backtest-distributions',
  'backtest-buckets',
  'backtest-symbols',
  'backtest-risk',
  'backtest-wins-losses',
  'backtest-streaks',
  'backtest-trade-chart',
  'backtest-trade-fills',
  'backtest-daily-pnl',
  'backtest-win-rate',
  'backtest-monthly',
  'backtest-monthly-table',
  'backtest-hold-times',
  'backtest-dist-hour',
  'backtest-dist-day',
  'backtest-dist-duration',
  'backtest-return-histogram',
  'backtest-day-metrics',
  'backtest-rolling-windows',
  'backtest-general-metrics',
  'backtest-equity-curve',
  'backtest-strategy-rollup',
  // ── Backtest run panels (per-execution workspace) ──────────────────────────
  'backtest-run-trades',
  'backtest-chart-grid',
  'backtest-filter-lab',
  'backtest-field-explorer',
  'backtest-monte-carlo',
  'backtest-monte-carlo-bands',
  'backtest-is-oos',
  'backtest-period-stability',
  'backtest-baseline',
  'backtest-sizing-settings',
  'backtest-sizing-equity',
  'backtest-not-taken',
  // ── Journal panels (the trader's real trades; /journal, journal-release) ────
  // The Journal also mounts the backtest report panels above through its data
  // provider (spec docs/superpowers/specs/2026-10-16-journal-design.md, D12).
  'journal-edge-rating',
  'journal-discipline',
  'journal-calendar',
  'journal-daily-pnl',
  'journal-day-metrics',
  'journal-day-trades',
  'journal-day-plans',
  'journal-day-pnl',
  'journal-recap',
  'journal-trades',
  'journal-trade-detail',
  'journal-annotation',
  'journal-plans',
  'journal-untaken-plans',
  'journal-exit-view',
  'journal-set-and-forget',
  'journal-mistakes',
  'journal-context-breakdown',
  'journal-context-scatter',
  'journal-population',
  'journal-backtest-live',
  'journal-lessons',
  'journal-ask',
])

export type ComponentType = z.infer<typeof ComponentTypeSchema>

/** Link mode controlling how ticker changes propagate between panels. */
export const LinkModeSchema = z.enum(['onChange', 'purple', 'blue', 'green', 'yellow', 'none'])

/** Per-window configuration stored alongside the mosaic tree. */
export const WindowConfigSchema = z.object({
  symbolIndex: z.number(),
  componentType: ComponentTypeSchema,
  /**
   * When false, the panel top bar (title, actions, more-menu) is collapsed to a
   * 6px drag-grip strip. Hovering the strip reveals the full header as an
   * absolute overlay so drag / delete / menu remain reachable.
   * Defaults to true (header visible) when absent.
   */
  headerVisible: z.boolean().optional(),
  linkMode: LinkModeSchema.optional(),
  tickerCleared: z.boolean().optional(),
})

export type WindowConfig = z.infer<typeof WindowConfigSchema>

/**
 * Mosaic tree node — recursive structure used by react-mosaic-component.
 * Either a leaf (string panel ID) or a branch (split with two children).
 */
export const MosaicNodeSchema: z.ZodType<MosaicNode> = z.lazy(() =>
  z.union([
    z.string(),
    z.object({
      direction: z.enum(['row', 'column']),
      first: MosaicNodeSchema,
      second: MosaicNodeSchema,
      splitPercentage: z.number().optional(),
    }),
  ]),
)

export type MosaicBranch = {
  direction: 'row' | 'column'
  first: MosaicNode
  second: MosaicNode
  splitPercentage?: number
}

export type MosaicNode = string | MosaicBranch

/**
 * A tab group — one mosaic pane hosting multiple components as tabs.
 * Keyed (in `tabGroups`) by the host mosaic-leaf panel ID. `tabs[0]` is the
 * host panel itself; additional tab IDs are extra panels owned by the group
 * (they exist in `windowConfigs`/`panels` but are NOT mosaic leaves).
 * A leaf with no `tabGroups` entry (or a single-tab entry) is a plain
 * single-component pane — i.e. today's behavior. Purely additive.
 */
export const TabGroupSchema = z.object({
  tabs: z.array(z.string()),
  activeTabId: z.string(),
})

export type TabGroup = z.infer<typeof TabGroupSchema>

// ─── Vertical grid layout (react-grid-layout) ───

/**
 * Which layout engine a layout renders with. Absent = 'mosaic' (the historical
 * default — every pre-existing doc). 'grid' selects the vertically-scrollable
 * react-grid-layout renderer. Per-layout: one workspace can hold a mix.
 */
export const LayoutModeSchema = z.enum(['mosaic', 'grid'])

export type LayoutMode = z.infer<typeof LayoutModeSchema>

/**
 * One react-grid-layout cell. Mirrors RGL's `Layout` item shape. `i` is the
 * panel ID (same key space as `windowConfigs`). `mosaicTree` and `gridLayout`
 * coexist in a doc — switching `layoutMode` never destroys the other.
 */
export const GridCellSchema = z.object({
  i: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  minW: z.number().optional(),
  minH: z.number().optional(),
})

export type GridCell = z.infer<typeof GridCellSchema>

// ─── Workspace Document (single atomic cache entry per layout) ───

/**
 * Single atomic workspace document — layout tree + configs + all panel states.
 * Stored in IndexedDB, one per (workspaceId, layoutId).
 *
 * `layoutRevision` is sourced from bootstrap and lets the workspace-store
 * trust IDB on init (skip the redundant loadLayout fetch) when the controller's
 * bootstrap promise has resolved with content for this layoutId.
 */
export const WorkspaceDocumentSchema = z.object({
  version: z.literal(2),
  /**
   * Mirror of the tree-item name. Optional for backwards compat with docs
   * that pre-date the field — the tree remains the authoritative source.
   * Every new write embeds the current name so docs converge on having it.
   */
  name: z.string().optional(),
  mosaicTree: MosaicNodeSchema.nullable(),
  windowConfigs: z.record(z.string(), WindowConfigSchema),
  globalSettings: z.record(z.unknown()),
  panels: z.record(z.string(), z.record(z.unknown())),
  /** Optional pane tab-groups, keyed by host leaf panel ID. Absent = all single-component panes. */
  tabGroups: z.record(z.string(), TabGroupSchema).optional(),
  /**
   * Which renderer this layout uses. Absent = 'mosaic'. Additive: old docs
   * omit it and default to mosaic. See LayoutModeSchema.
   */
  layoutMode: LayoutModeSchema.optional(),
  /**
   * react-grid-layout cell arrangement for 'grid' mode. Coexists with
   * `mosaicTree` — neither overwrites the other on a mode switch. Absent for
   * mosaic-only layouts.
   */
  gridLayout: z.array(GridCellSchema).optional(),
  layoutRevision: z.number().optional(),
})

export type WorkspaceDocument = z.infer<typeof WorkspaceDocumentSchema>

/** Single tree item stored in the flat adjacency map. */
export const LayoutTreeItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  isFolder: z.boolean(),
  children: z.array(z.string()),
})

/**
 * Workspace metadata — one per workspaceId.
 * Tracks workspaceRevision counter, active layout, and tree structure.
 *
 * Legacy `layouts`, `folders`, and `openFolderIds` fields were retired in the
 * unification refactor; `templates` moved to the chart-assets store (its own
 * localStorage snapshot). The bootstrap migration strips them on the server
 * side; any IDB cache still carrying them validates fine because Zod ignores
 * extras.
 */
export const WorkspaceMetaSchema = z.object({
  version: z.literal(2),
  workspaceRevision: z.number(),
  activeLayoutId: z.string().nullable(),
  treeItems: z.record(z.string(), LayoutTreeItemSchema).optional(),
  treeOpenState: z.array(z.string()).optional(),
})

export type WorkspaceMeta = z.infer<typeof WorkspaceMetaSchema>

// ─── Import/Export Schemas ───

export const ShareScopeSchema = z.enum(['dashboard', 'history'])
export type ShareScope = z.infer<typeof ShareScopeSchema>

/** Exported layout — full workspace with panels, encoded for URL sharing. */
export const LayoutExportSchema = z.object({
  exportType: z.literal('layout'),
  scope: ShareScopeSchema.default('dashboard'),
  version: z.literal(2),
  name: z.string(),
  mosaicTree: MosaicNodeSchema.nullable(),
  windowConfigs: z.record(z.string(), WindowConfigSchema),
  panels: z.record(z.string(), z.record(z.unknown())),
  globalSettings: z.record(z.unknown()).default({}),
  tabGroups: z.record(z.string(), TabGroupSchema).optional(),
  layoutMode: LayoutModeSchema.optional(),
  gridLayout: z.array(GridCellSchema).optional(),
  exportedAt: z.string(),
})

export type LayoutExport = z.infer<typeof LayoutExportSchema>

/** Exported profile — single panel config (filters/columns/aggregates/colors). */
export const ProfileExportSchema = z.object({
  exportType: z.literal('profile'),
  scope: ShareScopeSchema.default('dashboard'),
  version: z.literal(2),
  profileType: z.string(),
  name: z.string(),
  data: z.record(z.unknown()),
  subPanels: z.record(z.string(), z.record(z.unknown())).optional(),
  exportedAt: z.string(),
})

export type ProfileExport = z.infer<typeof ProfileExportSchema>
